/**
 * Detector Factory - Creates the appropriate detector based on environment
 *
 * ⚠️  IMPORTANT: This file is intentionally isolated and rarely changed.
 * ⚠️  When modifying this file, MANUAL TESTING is required:
 * ⚠️  1. Test in development with real YOLO model (verify ball/paddle detection)
 * ⚠️  2. Test in production build (verify model loads correctly)
 * ⚠️  3. Run cucumber tests to verify mock detector works in test mode
 *
 * In test mode (railsEnv === 'test'), we use a lightweight mock detector
 * to avoid loading TensorFlow.js and the YOLO model, which is slow and
 * unreliable in CI headless Chrome environments.
 */

import { TfjsYoloDetector } from './tfjsDetector';
import { TfjsYoloPoseDetector } from './tfjsYoloPoseDetector';
import { OnnxYoloDetector } from './onnxDetector';
import { OnnxYoloPoseDetector } from './onnxYoloPoseDetector';
import { OnnxMoveNetDetector } from './onnxMoveNetDetector';

/**
 * Single source of truth for inference engine options
 * Used by both the detector factory and the UI
 */
export const ENGINE_OPTIONS = [
  {
    id: 'tfjs-webgl',
    label: 'TF.js WebGL',
    icon: '🔧',
    description: 'Universal compatibility',
    needsWebGPU: false
  },
  {
    id: 'tfjs-webgpu',
    label: 'TF.js WebGPU',
    icon: '⚡',
    description: 'Best performance on Chrome',
    needsWebGPU: true
  },
  {
    id: 'onnx-webgpu',
    label: 'ONNX WebGPU',
    icon: '🚀',
    description: 'Alternative WebGPU runtime',
    needsWebGPU: true
  }
];

// Default engine for new sessions
export const DEFAULT_ENGINE = 'tfjs-webgl';

// Options for exercises that use pose detection.
// TF.js WebGL runs MoveNet (500KB). ONNX WebGPU runs YOLO11n-pose (10MB,
// but MoveNet-compatible keypoint output).
export const POSE_ONLY_ENGINE_OPTIONS = [
  { id: 'tfjs-webgl', label: 'TF.js WebGL', icon: '🔧', description: 'MoveNet pose detection', needsWebGPU: false },
  { id: 'onnx-webgpu', label: 'ONNX WebGPU', icon: '🚀', description: 'YOLO11n-pose on WebGPU', needsWebGPU: true }
];

/**
 * Mock YOLO Detector for test environments
 *
 * Implements the EXACT same interface as TfjsYoloDetector but:
 * - Does not load TensorFlow.js or process model weights
 * - Verifies model file exists (catches configuration errors)
 * - Returns empty detections (or can be configured to return mock detections)
 */
class MockYoloDetector {
  constructor(modelPath, options = {}) {
    // Same properties as TfjsYoloDetector
    this.modelPath = modelPath;
    this.inputSize = options.inputSize || 320;
    this.confThreshold = options.confThreshold || 0.15;
    this.iouThreshold = options.iouThreshold || 0.45;
    this.classNames = options.classNames || ['ball', 'paddle'];
    this.model = null;
    this.isInitialized = false;
    this.frameBuffer = [];

    // Mock-specific
    this._mockDetections = [];

    // Model info (mock values for testing)
    this.modelInfo = {
      modelName: 'mock-detector',
      modelJson: 'mock-model.json',
      deployedAt: null
    };
  }

  /**
   * Mock initialization - verifies model file exists but doesn't load/process it
   * This catches configuration errors (wrong path) without heavy TF.js processing
   * @param {number} timeout - Timeout in milliseconds (ignored in mock, kept for interface parity)
   */
  async initialize(timeout = 30000) {
    console.log(`[MockYoloDetector] Verifying model exists at: ${this.modelPath}`);

    // Verify model file exists (HEAD request to avoid downloading)
    try {
      const response = await fetch(this.modelPath, { method: 'HEAD' });
      if (!response.ok) {
        throw new Error(`Model file not found: ${this.modelPath} (status: ${response.status})`);
      }
      console.log('[MockYoloDetector] Model file verified, initialized (test mode - no processing)');
    } catch (error) {
      // If HEAD fails, try GET with abort (some servers don't support HEAD)
      try {
        const controller = new AbortController();
        const response = await fetch(this.modelPath, {
          method: 'GET',
          signal: controller.signal
        });
        // Abort immediately after headers received - we just want to verify file exists
        controller.abort();
        if (!response.ok) {
          throw new Error(`Model file not found: ${this.modelPath} (status: ${response.status})`);
        }
        console.log('[MockYoloDetector] Model file verified via GET, initialized (test mode - no processing)');
      } catch (fetchError) {
        // Ignore abort errors, re-throw others
        if (fetchError.name !== 'AbortError') {
          console.error('[MockYoloDetector] Failed to verify model file:', fetchError);
          throw new Error(`Model file not accessible: ${this.modelPath} - ${fetchError.message}`);
        }
        console.log('[MockYoloDetector] Model file verified, initialized (test mode - no processing)');
      }
    }

    this.isInitialized = true;
    return true;
  }

  /**
   * Mock detection - returns empty array or configured mock detections
   * Tests can use setMockDetections() to simulate specific scenarios
   */
  async detect(source) {
    if (!this.isInitialized) {
      throw new Error('Detector not initialized. Call initialize() first.');
    }
    return this._mockDetections;
  }

  /**
   * Set mock detections for testing specific scenarios
   * @param {Array} detections - Array of detection objects
   */
  setMockDetections(detections) {
    this._mockDetections = detections;
  }

  /**
   * Get model version info
   * @returns {Object} Model info (mock values)
   */
  getModelInfo() {
    return this.modelInfo;
  }

  /**
   * Stop the detector and clean up resources
   * Same interface as TfjsYoloDetector.stop()
   */
  async stop() {
    this.model = null;
    this.frameBuffer = [];
    this.isInitialized = false;
    this._mockDetections = [];
  }
}

/**
 * Create a YOLO detector appropriate for the current environment
 *
 * @param {string} modelPath - Path to YOLO model
 * @param {Object} options - Detector options
 * @param {string} options.railsEnv - Rails environment ('development', 'test', 'production')
 * @param {string} options.engine - Inference engine ('tfjs' or 'onnx', default: 'tfjs')
 * @param {string[]} options.classNames - Class names for detection
 * @param {number} options.inputSize - Model input size
 * @param {number} options.confThreshold - Confidence threshold
 * @returns {TfjsYoloDetector|OnnxYoloDetector|MockYoloDetector}
 */
export function createYoloDetector(modelPath, options = {}) {
  const { railsEnv = 'development', engine = 'tfjs-webgl', ...detectorOptions } = options;

  // Use mock detector in test mode to avoid slow/unreliable model loading in CI
  if (railsEnv === 'test') {
    console.log('[DetectorFactory] Using MockYoloDetector (test mode)');
    return new MockYoloDetector(modelPath, detectorOptions);
  }

  // Parse engine string: 'tfjs-webgpu', 'tfjs-webgl', 'onnx-webgpu'
  const [runtime, backend] = engine.split('-');

  // Use ONNX Runtime
  if (runtime === 'onnx') {
    console.log(`[DetectorFactory] Using OnnxYoloDetector (${backend})`);
    return new OnnxYoloDetector(modelPath, { ...detectorOptions, backend });
  }

  // Default to TensorFlow.js
  console.log(`[DetectorFactory] Using TfjsYoloDetector (${backend})`);
  return new TfjsYoloDetector(modelPath, { ...detectorOptions, backend });
}

export { MockYoloDetector };

/**
 * Mock YOLO Pose Detector for test environments
 *
 * Implements the EXACT same interface as TfjsYoloPoseDetector but:
 * - Does not load TensorFlow.js or process model weights
 * - Verifies model file exists (catches configuration errors)
 * - Returns empty poses (or can be configured to return mock poses)
 */
class MockYoloPoseDetector {
  constructor(modelPath, options = {}) {
    this.modelPath = modelPath;
    this.inputSize = options.inputSize || 640;
    this.confThreshold = options.confThreshold || 0.25;
    this.iouThreshold = options.iouThreshold || 0.45;
    this.model = null;
    this.isInitialized = false;

    // Mock-specific
    this._mockPoses = [];

    // Model info (mock values for testing)
    this.modelInfo = {
      modelName: 'mock-pose-detector',
      modelJson: 'mock-pose-model.json',
      deployedAt: null
    };
  }

  /**
   * Mock initialization - verifies model file exists but doesn't load/process it
   */
  async initialize(timeout = 30000) {
    console.log(`[MockYoloPoseDetector] Verifying model exists at: ${this.modelPath}`);

    try {
      const response = await fetch(this.modelPath, { method: 'HEAD' });
      if (!response.ok) {
        throw new Error(`Model file not found: ${this.modelPath} (status: ${response.status})`);
      }
      console.log('[MockYoloPoseDetector] Model file verified, initialized (test mode - no processing)');
    } catch (error) {
      // If HEAD fails, try GET with abort
      try {
        const controller = new AbortController();
        const response = await fetch(this.modelPath, {
          method: 'GET',
          signal: controller.signal
        });
        controller.abort();
        if (!response.ok) {
          throw new Error(`Model file not found: ${this.modelPath} (status: ${response.status})`);
        }
        console.log('[MockYoloPoseDetector] Model file verified via GET, initialized (test mode - no processing)');
      } catch (fetchError) {
        if (fetchError.name !== 'AbortError') {
          console.error('[MockYoloPoseDetector] Failed to verify model file:', fetchError);
          throw new Error(`Model file not accessible: ${this.modelPath} - ${fetchError.message}`);
        }
        console.log('[MockYoloPoseDetector] Model file verified, initialized (test mode - no processing)');
      }
    }

    this.isInitialized = true;
    return true;
  }

  /**
   * Mock pose estimation - returns empty array or configured mock poses
   * Output format matches TfjsYoloPoseDetector (MoveNet-compatible)
   */
  async estimatePoses(source) {
    if (!this.isInitialized) {
      throw new Error('[MockYoloPoseDetector] Detector not initialized. Call initialize() first.');
    }
    return this._mockPoses;
  }

  /**
   * Set mock poses for testing specific scenarios
   * @param {Array} poses - Array of pose objects with keypoints
   */
  setMockPoses(poses) {
    this._mockPoses = poses;
  }

  getModelInfo() {
    return this.modelInfo;
  }

  async stop() {
    this.model = null;
    this.isInitialized = false;
    this._mockPoses = [];
  }
}

/**
 * Create a YOLO Pose detector appropriate for the current environment
 *
 * @param {string} modelPath - Path to YOLO pose model
 * @param {Object} options - Detector options
 * @param {string} options.railsEnv - Rails environment ('development', 'test', 'production')
 * @param {string} options.engine - Inference engine ('tfjs' or 'onnx', default: 'tfjs')
 * @param {number} options.inputSize - Model input size
 * @param {number} options.confThreshold - Confidence threshold
 * @returns {TfjsYoloPoseDetector|OnnxYoloPoseDetector|MockYoloPoseDetector}
 */
export function createYoloPoseDetector(modelPath, options = {}) {
  // poseModel='yolo' by default — MoveNet ONNX is blocked by an ORT WebGPU
  // GatherND kernel bug (see comments in onnxMoveNetDetector.js). YOLO11n-pose
  // works on WebGPU and returns MoveNet-compatible keypoints.
  const { railsEnv = 'development', engine = 'tfjs-webgl', poseModel = 'movenet', ...detectorOptions } = options;

  if (railsEnv === 'test') {
    console.log('[DetectorFactory] Using MockYoloPoseDetector (test mode)');
    return new MockYoloPoseDetector(modelPath, detectorOptions);
  }

  // Parse engine string: 'tfjs-webgpu', 'tfjs-webgl', 'onnx-webgpu'
  const [runtime, backend] = engine.split('-');

  if (runtime === 'onnx') {
    // Prefer MoveNet-thunder ONNX for skipping — comparable size/shape to
    // TF.js MoveNet, ~20× smaller than YOLO11n-pose. Fall back to YOLO pose
    // only when explicitly requested (poseModel === 'yolo').
    if (poseModel === 'movenet') {
      console.log(`[DetectorFactory] Using OnnxMoveNetDetector (${backend})`);
      return new OnnxMoveNetDetector(modelPath, { ...detectorOptions, backend });
    }
    console.log(`[DetectorFactory] Using OnnxYoloPoseDetector (${backend})`);
    return new OnnxYoloPoseDetector(modelPath, { ...detectorOptions, backend });
  }

  console.log(`[DetectorFactory] Using TfjsYoloPoseDetector (${backend})`);
  return new TfjsYoloPoseDetector(modelPath, { ...detectorOptions, backend });
}

export { MockYoloPoseDetector };
